import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { StyleProp, TextStyle, ViewStyle } from 'react-native';
import {
  LayoutAnimation,
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  TextLayoutEventData,
  TextLayoutLine,
  UIManager,
  View,
} from 'react-native';

import { Text, type TextProps } from '@/components/ui/text';
import { STRINGS } from '@/constants/strings';
import { guardianColors } from '@/theme/colors';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export type ExpandableTextProps = {
  children: string;
  /** Lines visible while collapsed (default 3). */
  collapsedLines?: number;
  /** Applies the design-system typography variant (font, size, line-height). */
  variant?: TextProps['variant'];
  /** Additional style applied to the text content. */
  textStyle?: StyleProp<TextStyle>;
  /** Toggle labels — default to the shared "View more" / "Show less" copy. */
  viewMoreLabel?: string;
  showLessLabel?: string;
  /** Kept for API compatibility. */
  toggleVariant?: TextProps['variant'];
  /** Style applied to the "View more" / "Show less" toggle. */
  toggleStyle?: StyleProp<TextStyle>;
  /** Style applied to the outer container View. */
  style?: StyleProp<ViewStyle>;
  /** Extra characters to trim from the last line to fit the "View more" label. */
  trimBuffer?: number;
  /** Background color used to mask text on Web when placing the toggle inline. */
  webMaskColor?: string;
};

function WebExpandableText(props: ExpandableTextProps) {
  const {
    children,
    collapsedLines = 3,
    variant = 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    textStyle,
    viewMoreLabel = STRINGS.common.viewMore,
    showLessLabel = STRINGS.common.showLess,
    toggleVariant,
    toggleStyle,
    style,
  } = props;

  const [expanded, setExpanded] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const [finalLimit, setFinalLimit] = useState<number | null>(null);
  const measureRef = useRef<any>(null);

  const isTruncatable = fullHeight > collapsedHeight + 4;
  const toggleExpand = () => setExpanded(!expanded);

  // Synchronous binary search to find the EXACT character limit
  // that fits within the collapsedHeight without overflowing.
  useLayoutEffect(() => {
    if (!isTruncatable || finalLimit !== null || !measureRef.current || Platform.OS !== 'web')
      return;

    const containerNode = measureRef.current;
    const textNode = containerNode.firstChild;
    if (!textNode) return;

    let low = 0;
    let high = children.length;
    let best = low;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      // Synchronously mutate DOM to measure text wrapping exactness
      textNode.textContent = children.substring(0, mid) + '... ' + viewMoreLabel;

      if (textNode.clientHeight <= collapsedHeight + 4) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // Clean up measurement node
    textNode.textContent = '';

    // Snap to last space to avoid cutting words in half
    let sliced = children.substring(0, best);
    const lastSpace = sliced.lastIndexOf(' ');
    if (lastSpace > best * 0.8) {
      best = lastSpace;
    }

    setFinalLimit(best);
  }, [isTruncatable, finalLimit, children, collapsedHeight, viewMoreLabel]);

  const webDisplayText = useMemo(() => {
    if (!isTruncatable || expanded || finalLimit === null) return children;
    return children.substring(0, finalLimit).trimEnd() + '... ';
  }, [children, isTruncatable, expanded, finalLimit]);

  return (
    <View style={style}>
      {/* Hidden text for measuring FULL height */}
      <Text
        variant={variant}
        style={[textStyle, styles.hidden]}
        onLayout={e => setFullHeight(e.nativeEvent.layout.height)}>
        {children}
      </Text>

      {/* Hidden text for measuring COLLAPSED height (max 3 lines) */}
      <Text
        variant={variant}
        style={[textStyle, styles.hidden]}
        numberOfLines={collapsedLines}
        onLayout={e => setCollapsedHeight(e.nativeEvent.layout.height)}>
        {children}
      </Text>

      {/* Hidden view node used exclusively for synchronous binary search */}
      {isTruncatable && finalLimit === null && !expanded && (
        <View ref={measureRef} style={styles.hidden}>
          <Text variant={variant} style={textStyle}>
            {children}
          </Text>
        </View>
      )}

      {(!isTruncatable || expanded || finalLimit !== null) && (
        <Text
          variant={variant}
          style={textStyle}
          // Remove native restriction when truncated so the toggle is never hidden
          numberOfLines={expanded ? undefined : undefined}>
          {webDisplayText}
          {isTruncatable && !expanded && (
            <Text
              variant={toggleVariant ?? variant}
              style={[styles.toggle, toggleStyle]}
              onPress={toggleExpand}>
              {viewMoreLabel}
            </Text>
          )}
        </Text>
      )}

      {isTruncatable && expanded && (
        <Text
          variant={toggleVariant ?? variant}
          style={[styles.toggle, toggleStyle, styles.showLessSpacing]}
          onPress={toggleExpand}>
          {showLessLabel}
        </Text>
      )}
    </View>
  );
}

function NativeExpandableText(props: ExpandableTextProps) {
  const {
    children,
    collapsedLines = 3,
    variant = 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    textStyle,
    viewMoreLabel = STRINGS.common.viewMore,
    showLessLabel = STRINGS.common.showLess,
    toggleVariant,
    toggleStyle,
    style,
    trimBuffer = 4, // 3 dots + 1 space
  } = props;
  const [expanded, setExpanded] = useState(false);
  const [measuredLines, setMeasuredLines] = useState<TextLayoutLine[] | null>(null);

  // Measure the full text exactly once when `children` changes
  const handleTextLayout = useCallback(
    (e: NativeSyntheticEvent<TextLayoutEventData>) => {
      if (!measuredLines || measuredLines.length === 0) {
        setMeasuredLines(e.nativeEvent.lines);
      }
    },
    [measuredLines],
  );

  // Reset measurements if text changes
  const previousChildren = React.useRef(children);
  if (previousChildren.current !== children) {
    previousChildren.current = children;
    setMeasuredLines(null);
  }

  const isTruncatable = measuredLines && measuredLines.length > collapsedLines;

  // Calculate the custom truncated text by taking the first N-1 lines completely,
  // and manually trimming the last visible line so "View more" fits exactly inline.
  const displayText = useMemo(() => {
    if (!measuredLines || measuredLines.length === 0) return '';
    if (!isTruncatable || expanded) return children;

    const visibleLines = measuredLines.slice(0, collapsedLines);
    const firstLines = visibleLines
      .slice(0, -1)
      .map(l => l.text)
      .join('');
    const lastLine = visibleLines[visibleLines.length - 1];

    // Number of characters to trim from the last line
    // We assume "View more" characters have roughly the same width as standard characters
    const charsToRemove = viewMoreLabel.length + trimBuffer;

    let truncatedLastLine = lastLine.text;
    // Remove trailing newline if any, so it doesn't drop down
    if (truncatedLastLine.endsWith('\n')) {
      truncatedLastLine = truncatedLastLine.slice(0, -1);
    }

    if (truncatedLastLine.length > charsToRemove) {
      truncatedLastLine = truncatedLastLine.slice(0, -charsToRemove);
    } else {
      // In a rare case where the last line is extremely short, just drop it entirely
      truncatedLastLine = '';
    }

    return firstLines + truncatedLastLine.trimEnd() + '... ';
  }, [measuredLines, isTruncatable, expanded, children, collapsedLines, viewMoreLabel, trimBuffer]);

  const toggleExpand = useCallback(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpanded(prev => !prev);
  }, []);

  if (!measuredLines) {
    return (
      <View style={style}>
        {/* Hidden text to calculate precise layout lines */}
        <Text variant={variant} style={[textStyle, styles.hidden]} onTextLayout={handleTextLayout}>
          {children}
        </Text>
      </View>
    );
  }

  return (
    <View style={style}>
      <Text variant={variant} style={textStyle}>
        {displayText}
        {isTruncatable && !expanded && (
          <Text
            variant={toggleVariant ?? variant}
            style={[styles.toggle, toggleStyle]}
            onPress={toggleExpand}>
            {viewMoreLabel}
          </Text>
        )}
      </Text>
      {expanded && (
        <Text
          variant={toggleVariant ?? variant}
          style={[styles.toggle, toggleStyle, styles.showLessSpacing]}
          onPress={toggleExpand}>
          {showLessLabel}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  hidden: {
    position: 'absolute',
    opacity: 0,
  },
  toggle: {
    color: guardianColors.Blue,
    textDecorationLine: 'underline',
  },
  showLessSpacing: {
    marginTop: 0,
  },
});

export const ExpandableText = memo((props: ExpandableTextProps) => {
  if (Platform.OS === 'web') {
    return <WebExpandableText {...props} />;
  }
  return <NativeExpandableText {...props} />;
});

export default ExpandableText;
